import { FormApi, ReactFormApi } from "@tanstack/react-form";
import { CrudFormModel } from "./crudFormModel";
import React from "react";
import { ZodObject, ZodRawShape, z } from "zod";
import { CrudTextInput } from "./inputs/crudTextInput";
import { Validator } from "../../../domain/models/formValidator";
import { CrudCheckboxInput } from "./inputs/crudCheckboxInput";
import { CrudNumberInput } from "./inputs/crudNumberInput";
import { useTranslation } from "react-i18next";
import { CrudEnumInput } from "./inputs/crudEnumInput";
import { TFunction } from "i18next";
import { CrudDateInput } from "./inputs/crudDateInput";
import { CrudCustomPlaceholderInput } from "./inputs/crudCustomInput";
import { CrudPasswordInput } from "./inputs/crudPasswordInput";
import { CrudSelectInput } from "./inputs/crudSelectInput";
import { datePickerOptions } from "./inputs/crudInputModel";
import { EntitySelectFieldModel } from "../../../fields/modules/commons/entitySelectField/entitySelectFieldModel";
import { CrudEntitySelectInput } from "./inputs/crudEntitySelectInput";

const getFieldType = (def: z.ZodType<unknown>): z.ZodType<unknown> => {
  if ("innerType" in def._def) {
    return getFieldType(def._def.innerType as z.ZodType<unknown>);
  }

  return def;
};

export const generateInput = (
  def: z.ZodType<unknown>,
  key: number,
  form: FormApi<
    z.infer<ZodObject<ZodRawShape>>,
    Validator<z.infer<ZodObject<ZodRawShape>>, unknown>
  > &
    ReactFormApi<
      z.infer<ZodObject<ZodRawShape>>,
      Validator<z.infer<ZodObject<ZodRawShape>>, unknown>
    >,
  name: string,
  label: string,
  disabled: boolean,
  t: TFunction<"translation", undefined>,
  inputGroupText?: string,
  password?: boolean,
  selectOption?: {
    label: string;
    value: string | number;
    isDisabled: boolean;
  }[],
  datePickerOptions?: datePickerOptions,
  entitySelectOptions?: EntitySelectFieldModel,
) => {
  const fieldType = getFieldType(def);
  const required = !(def instanceof z.ZodNullable);

  if (fieldType instanceof z.ZodString && password) {
    return (
      <CrudPasswordInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
      />
    );
  }
  if (fieldType instanceof z.ZodString) {
    if (selectOption != undefined) {
      return (
        <CrudSelectInput
          key={key}
          validationSchema={def}
          form={form}
          name={name}
          label={label}
          disabled={disabled}
          t={t}
          inputGroupText={inputGroupText}
          required={required}
          selectOption={selectOption}
          isMulti={false}
        />
      );
    } else if (entitySelectOptions != undefined) {
      return (
        <CrudEntitySelectInput
          key={key}
          validationSchema={def}
          form={form}
          name={name}
          label={label}
          disabled={disabled}
          t={t}
          inputGroupText={inputGroupText}
          required={required}
          entitySelectOptions={entitySelectOptions}
        />
      );
    }

    return (
      <CrudTextInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
      />
    );
  } else if (fieldType instanceof z.ZodNumber) {
    if (selectOption != undefined) {
      return (
        <CrudSelectInput
          key={key}
          validationSchema={def}
          form={form}
          name={name}
          label={label}
          disabled={disabled}
          t={t}
          inputGroupText={inputGroupText}
          required={required}
          selectOption={selectOption}
          isMulti={false}
        />
      );
    } else if (entitySelectOptions != undefined) {
      return (
        <CrudEntitySelectInput
          key={key}
          validationSchema={def}
          form={form}
          name={name}
          label={label}
          disabled={disabled}
          t={t}
          inputGroupText={inputGroupText}
          required={required}
          entitySelectOptions={entitySelectOptions}
        />
      );
    }

    return (
      <CrudNumberInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
      />
    );
  } else if (fieldType instanceof z.ZodBoolean) {
    return (
      <CrudCheckboxInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
      />
    );
  } else if (fieldType instanceof z.ZodEnum) {
    return (
      <CrudEnumInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
      />
    );
  } else if (fieldType instanceof z.ZodArray) {
    return (
      <CrudSelectInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
        selectOption={selectOption}
        isMulti={true}
      />
    );
  } else if (fieldType instanceof z.ZodDate) {
    return (
      <CrudDateInput
        key={key}
        validationSchema={def}
        form={form}
        name={name}
        label={label}
        disabled={disabled}
        t={t}
        inputGroupText={inputGroupText}
        required={required}
        datePickerOptions={datePickerOptions}
      />
    );
  } else {
    if (def === undefined) {
      return (
        <p key={key}>
          The field &apos;{key}&apos; doesn&apos;t exist in the schema
        </p>
      );
    } else {
      return (
        <p key={key}>
          Type {def.constructor.name} is not supported in the form generator
        </p>
      );
    }
  }
};

const generateInputs = (
  schema: ZodObject<ZodRawShape>,
  form: FormApi<
    z.infer<typeof schema>,
    Validator<z.infer<typeof schema>, unknown>
  > &
    ReactFormApi<
      { [x: string]: unknown },
      Validator<{ [x: string]: unknown }, unknown>
    >,
  prefix: string,
  disabledFields: (string | undefined)[] | undefined,
  hiddenFields?: (string | undefined)[] | undefined,
  inputGroupField?: { [key: string]: string },
  selectOptions?: {
    [key: string]: {
      label: string;
      value: string | number;
      isDisabled: boolean;
    }[];
  },
  datePickerOptions?: { [key: string]: datePickerOptions },
) => {
  const formSchema = schema.omit({ id: true });

  const { t } = useTranslation("common");

  const inputs = Object.entries(formSchema.shape).map(([name, def], i) => {
    if (hiddenFields?.includes(name)) {
      return null;
    }
    const label = t(`crud.${prefix}.${name}`);
    return generateInput(
      def,
      i,
      form,
      name,
      label,
      disabledFields?.includes(name) ?? false,
      t,
      inputGroupField?.[name],
      false,
      selectOptions != undefined && selectOptions[name] != undefined
        ? selectOptions[name]
        : undefined,
      datePickerOptions != undefined && datePickerOptions[name] != undefined
        ? datePickerOptions[name]
        : undefined,
    );
  });

  return inputs;
};

function recursiveMap(
  children: React.ReactNode | React.ReactNode[],
  fn: (child: React.ReactElement) => React.ReactNode,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    if (child.props.children) {
      child = React.cloneElement(
        child as React.ReactElement<
          React.ReactNode | React.ReactNode[],
          | string
          | React.JSXElementConstructor<React.ReactNode | React.ReactNode[]>
        >,
        {
          children: recursiveMap(child.props.children, fn),
        },
      );
    }

    return fn(child);
  });
}

export const CrudForm = ({
  formTemplate,
  schema,
  prefix,
  form,
  disabledFields,
  hiddenFields,
  inputGroupFields,
  selectOptions,
  datePickerOptions,
  entitySelectOptions,
}: CrudFormModel) => {
  const { t } = useTranslation("common");

  const inputs =
    formTemplate !== undefined
      ? recursiveMap(formTemplate(), (child) => {
          if (child.type == CrudCustomPlaceholderInput) {
            const def = schema.shape[child.props.name];
            return React.cloneElement(child, {
              props: {
                validationSchema: def,
                form,
                name: child.props.name,
                label: t(`crud.${prefix}.${child.props.name}`),
                disabled: disabledFields?.includes(child.props.name) ?? false,
                t,
              },
            });
          }
          if (
            child.props.name !== undefined &&
            !hiddenFields?.includes(child.props.name)
          ) {
            const def = schema.shape[child.props.name];
            return generateInput(
              def,
              child.props.name,
              form,
              child.props.name,
              t(`crud.${prefix}.${child.props.name}`),
              disabledFields?.includes(child.props.name) ?? false,
              t,
              inputGroupFields?.[child.props.name],
              child.props.password,
              selectOptions != undefined &&
                selectOptions[child.props.name] != undefined
                ? selectOptions[child.props.name]
                : undefined,
              datePickerOptions != undefined &&
                datePickerOptions[child.props.name] != undefined
                ? datePickerOptions[child.props.name]
                : undefined,
              entitySelectOptions != undefined &&
                entitySelectOptions[child.props.name] != undefined
                ? entitySelectOptions[child.props.name]
                : undefined,
            );
          }
          return child;
        })
      : generateInputs(
          schema,
          form,
          prefix,
          disabledFields,
          hiddenFields,
          inputGroupFields,
        );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {inputs}
    </form>
  );
};
